import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";

export const FORMAS_PAGAMENTO = [
  { value: "pix", label: "Pix" },
  { value: "dinheiro", label: "Dinheiro" },
  { value: "credito", label: "Cartão de Crédito" },
  { value: "debito", label: "Cartão de Débito" },
  { value: "link", label: "Link de Pagamento" },
  { value: "transferencia", label: "Transferência" },
];

export const CATEGORIAS_BASE = {
  studio: {
    entrada: ["Faturamento de Tatuagem", "Outros"],
    saida: ["Despesas Fixas", "Despesas Variáveis", "Fornecedores", "Materiais"]
  },
  pessoal: {
    receita: ["Salário", "Freelance", "Investimentos", "Outros"],
    despesa: ["Moradia", "Alimentação", "Transporte", "Saúde", "Academia", "Assinaturas", "Lazer", "Viagem", "Investimentos", "Outros"]
  }
};

interface FinanceiroFormProps {
  tipo_aba: "studio" | "pessoal";
  fixedTipo?: "entrada" | "saida" | "pessoal";
  categoriasExtras?: string[];
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  title: string;
}

export function FinanceiroForm({ tipo_aba, fixedTipo, categoriasExtras, initialData, onSubmit, onCancel, title }: FinanceiroFormProps) {
  const [tipo, setTipo] = useState<"entrada" | "saida" | "pessoal">(
    tipo_aba === "pessoal"
      ? "pessoal"
      : (fixedTipo || initialData?.tipo || "entrada")
  );

  const [formData, setFormData] = useState({
    descricao: initialData?.descricao || "",
    categoria: initialData?.categoria || "",
    valor: initialData?.valor || "",
    data: initialData?.data || new Date().toISOString().split('T')[0],
    recorrente: initialData?.recorrente || false,
    observacoes: initialData?.observacoes || "",
    forma_pagamento: initialData?.forma_pagamento || "",
  });

  const isSaida = tipo_aba === "studio" ? tipo === "saida" : fixedTipo === "saida";

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const currentCategories = tipo_aba === "studio"
    ? (tipo === "entrada" ? CATEGORIAS_BASE.studio.entrada : [...CATEGORIAS_BASE.studio.saida, ...(categoriasExtras || [])])
    : (fixedTipo === "entrada" ? CATEGORIAS_BASE.pessoal.receita : [...CATEGORIAS_BASE.pessoal.despesa, ...(categoriasExtras || [])]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, tipo });
  };

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-lg mx-auto shadow-2xl animate-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-white/5">
          <X size={24} />
        </Button>
      </div>

      {tipo_aba === "studio" && !fixedTipo && (
        <div className="flex items-center justify-center gap-4 mb-8 bg-background p-1 rounded-xl border border-border w-fit mx-auto">
          <button
            type="button"
            onClick={() => {
              setTipo("entrada");
              handleChange("categoria", "");
            }}
            className={cn(
              "px-6 py-2 rounded-lg text-sm font-semibold transition-all",
              tipo === "entrada" ? "bg-primary text-white shadow-lg" : "text-muted-foreground hover:text-zinc-300"
            )}
          >
            Entrada
          </button>
          <button
            type="button"
            onClick={() => {
              setTipo("saida");
              handleChange("categoria", "");
            }}
            className={cn(
              "px-6 py-2 rounded-lg text-sm font-semibold transition-all",
              tipo === "saida" ? "bg-primary text-white shadow-lg" : "text-muted-foreground hover:text-zinc-300"
            )}
          >
            Saída
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Data</Label>
          <DatePicker
            value={formData.data}
            onChange={(v) => handleChange("data", v)}
            className="h-11"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Descrição</Label>
          <Input 
            value={formData.descricao}
            onChange={(e) => handleChange("descricao", e.target.value)}
            placeholder="Ex: Aluguel do Studio, Compra de Tintas"
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Categoria</Label>
            <Select 
              value={formData.categoria} 
              onValueChange={(v) => handleChange("categoria", v)}
              required
            >
              <SelectTrigger className="bg-background border-border focus:ring-primary h-11">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                {currentCategories.map(cat => (
                  <SelectItem key={cat} value={cat} className="focus:bg-primary focus:text-white">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Valor</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
              <Input 
                type="number"
                step="0.01"
                value={formData.valor}
                onChange={(e) => handleChange("valor", e.target.value)}
                placeholder="0,00"
                className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 pl-10"
                required
              />
            </div>
          </div>
        </div>

        {isSaida && (
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm font-medium">Forma de Pagamento</Label>
            <Select
              value={formData.forma_pagamento}
              onValueChange={(v) => handleChange("forma_pagamento", v)}
            >
              <SelectTrigger className="bg-background border-border focus:ring-primary h-11">
                <SelectValue placeholder="Selecione (opcional)" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                {FORMAS_PAGAMENTO.map(fp => (
                  <SelectItem key={fp.value} value={fp.value} className="focus:bg-primary focus:text-white">
                    {fp.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-[#A1A1AA] text-sm font-medium">Observações</Label>
          <Textarea 
            value={formData.observacoes}
            onChange={(e) => handleChange("observacoes", e.target.value)}
            placeholder="Alguma informação adicional..."
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary min-h-[80px] resize-none"
          />
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
          <div className="space-y-0.5">
            <Label className="text-sm font-normal">Lançamento Recorrente?</Label>
            <p className="text-xs text-muted-foreground">Repetir todo mês automaticamente</p>
          </div>
          <Switch 
            checked={formData.recorrente}
            onCheckedChange={(v) => handleChange("recorrente", v)}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            type="submit" 
            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-12 transition-all"
          >
            Salvar Lançamento
          </Button>
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="px-6 border-border bg-transparent text-[#A1A1AA] hover:bg-white/5 hover:text-foreground h-12 transition-all"
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
